import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';

import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '@mds/shared/lib/api';
import {
  FREE_TRIAL_BOOKING_END_DATE,
  FREE_TRIAL_BOOKING_START_DATE,
  FREE_TRIAL_BOOKING_WINDOW_LABEL,
  FREE_TRIAL_EARLIEST_END_TIME,
  FREE_TRIAL_END_TIME,
  FREE_TRIAL_LATEST_START_TIME,
  FREE_TRIAL_START_TIME,
  FREE_TRIAL_STEP_MINUTES,
  FREE_TRIAL_TIME_WINDOW_LABEL,
  isFreeTrialBookingDate,
} from '@mds/shared/lib/free-trial';
import { SeatMap, normaliseDeskId, type SpaceId } from '@mds/shared/seat-map';
import type { BookingPaymentMethod, CreateAdminBookingRequest } from '@mds/shared/types/booking';
import type { AdminResource } from '@mds/shared/types/resource';
import BookingCalendar, { type Selection } from '@mds/shared/ui/booking-calendar';
import { runAvailable } from '@mds/shared/lib/booking-slots';

import { useSession } from '@/auth/use-session';
import * as bookings from '@/bookings/bookings.api';
import BookingDetails from '@/components/booking/booking-details';
import PackagePicker from '@/components/booking/package-picker';
import PaymentFields from '@/components/booking/payment-fields';
import CustomerForm from '@/components/customers/customer-form';
import CustomerPicker from '@/components/customers/customer-picker';
import {
  blankDraft,
  draftOfResource,
  slugFromName,
  operatingHoursOf,
  resourceSchema,
  type ResourceDraft,
} from '@/components/spaces/resource-draft';
import ResourceEditor from '@/components/spaces/resource-editor';
import Button from '@/components/ui/button';
import Card from '@/components/ui/card';
import Chip from '@/components/ui/chip';
import EmptyState from '@/components/ui/empty-state';
import Field, { controlCls, inputCls, selectCls } from '@/components/ui/field';
import Modal from '@/components/ui/modal';
import Select from '@mds/shared/ui/select';
import StatCard from '@/components/ui/stat-card';
import { Toolbar } from '@/components/ui/toolbar';
import { minRun, priceFor } from '@/domain/booking-slots';
import {
  DEPOSIT_PERCENTAGE,
  PAYMENT_METHOD_OPTIONS,
  blankPaymentDraft,
  bookingSchema,
  cancelSchema,
  collectableAmount,
  eligibilityPayload,
  depositAmount,
  isBookingConflict,
  isPaymentStateChanged,
  packageChoiceSchema,
  paymentSchema,
  type PaymentDraft,
} from '@/domain/bookings';
import { blankCustomer, createRequestOf, customerSchema } from '@/domain/customers';
import { clockRange, dateStamp, minutesOf, todayInTimeZone } from '@mds/shared/lib/dates';
import { formatCents } from '@/lib/money';
import { useValidation } from '@/lib/validation';
import * as rateCard from '@/rate-card/rate-card.api';
import * as resources from '@/resources/resources.api';

/**
 * How tall the floor plan is allowed to be. A tall window gives it whatever is
 * left below the chrome above it — app header, KPI row, card chrome, rotation
 * bar — and a short one falls back to the floor and scrolls instead: sizing it
 * to always fit leaves the plan too small to pick a desk off.
 */
const MAP_HEIGHT = 'max(460px, calc(100vh - 460px))';
const FREE_TRIAL_PACKAGE_ITEM_ID = 'free-trial';
const FACILITY_TIMEZONE = 'Asia/Beirut';

/**
 * Size the shared seat map to the space this page has for it. The width follows
 * the height so the canvas hugs the artwork rather than stranding it in a
 * full-width box; the slack left over reads as padding down its sides.
 *
 * Colour is left to the map's own defaults. Its tokens are declared on the
 * .seatmap element itself, so they can only be overridden here, on that same
 * element — and the legend baked into the artwork is fixed dark-on-light, so
 * pointing the canvas at a themed surface makes it unreadable in dark mode.
 */
const MAP_LAYOUT = {
  '--map-max-height': MAP_HEIGHT,
  '--map-canvas-max-width': `calc(${MAP_HEIGHT} * 1.6)`,
  '--map-canvas-gutter': '20px',
} as CSSProperties;

type Mode = 'existing' | 'new';

/**
 * What is being taken at the counter. The deposit is the fixed half the policy
 * names, never a typed figure, and `none` leaves the booking unpaid — which
 * holds nothing until someone pays.
 */
type Settle = 'full' | 'deposit' | 'none';

const blankForm = {
  customerId: '',
  /** New-customer mode: the same record the Customers page adds. */
  customer: blankCustomer,
  settle: 'full' as Settle,
  method: 'cash' as BookingPaymentMethod,
  methodDetail: '',
  note: '',
  /** Student packages only: what was checked. Required before any money is taken. */
  evidenceNote: '',
};

/** Book a desk from the floor plan, or a room by the hour from the timeline. */
export default function SpacesPage() {
  const queryClient = useQueryClient();
  const { can } = useSession();
  const mayManage = can('facilities.manage');
  const mayRead = can('reservations.read');
  const mayBook = can('reservations.manage');
  const mayRecord = can('payments.record');
  const maySeeCustomers = can('crm.customers.read');

  const inventory = useQuery({
    queryKey: ['resources', { includeInactive: mayManage }],
    queryFn: () => resources.listResources({ includeInactive: mayManage || undefined }),
  });
  const card = useQuery({ queryKey: ['rate-card'], queryFn: rateCard.getRateCard });

  const [date, setDate] = useState(() => todayInTimeZone(FACILITY_TIMEZONE));
  const [resourceId, setResourceId] = useState<string | null>(null);

  /** Desk id on a floor, or an hour range in a room. */
  const [desk, setDesk] = useState<string | null>(null);
  const [slot, setSlot] = useState<Selection | null>(null);
  /** The booking open in the side panel — a held desk, or a taken room slot. */
  const [openBookingId, setOpenBookingId] = useState<string | null>(null);
  /** Rooms only: set by Continue, once the run is as long as it should be. */
  const [runReady, setRunReady] = useState(false);

  /** Which desk package the customer is buying, on a coworking floor. */
  const [packageId, setPackageId] = useState('');
  const [trialStartTime, setTrialStartTime] = useState('');
  const [trialEndTime, setTrialEndTime] = useState('');

  const [formOpen, setFormOpen] = useState(false);
  /** A free desk was clicked: its booking popup stays open while the package changes. */
  const [choosing, setChoosing] = useState(false);
  const [mode, setMode] = useState<Mode>('existing');
  const [form, setForm] = useState(blankForm);
  /** New-customer mode checks the same record the Customers page does. */
  const customerValidation = useValidation(customerSchema, form.customer);
  const [booking, setBooking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [collecting, setCollecting] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [payment, setPayment] = useState<PaymentDraft>(blankPaymentDraft);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [panelError, setPanelError] = useState<string | null>(null);

  const [draft, setDraft] = useState<ResourceDraft | null>(null);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editorError, setEditorError] = useState<string | null>(null);
  const [deskBusyId, setDeskBusyId] = useState<string | null>(null);
  const editorErrorRef = useRef<HTMLParagraphElement>(null);

  // The editor is long enough to scroll, and Save sits at the bottom of it, so
  // a rejection at the top would otherwise land off-screen.
  useEffect(() => {
    if (editorError) editorErrorRef.current?.scrollIntoView({ block: 'nearest' });
  }, [editorError]);

  const all = useMemo(() => inventory.data ?? [], [inventory.data]);
  const resource = useMemo(
    () => all.find((r) => r.id === resourceId) ?? all[0] ?? null,
    [all, resourceId],
  );

  // Stable identities, or the effect below re-runs on every render.
  // An off-sale package stays on the card for history but is refused for a new
  // booking, so it is never offered here.
  const deskPackages = useMemo(
    () => (card.data?.deskPackages ?? []).filter((p) => p.package.isAvailable),
    [card.data],
  );
  const spaceRates = useMemo(() => card.data?.spaceRates ?? [], [card.data]);

  // Seed the two pickers once their lists arrive.
  useEffect(() => {
    if (!resourceId && all.length > 0) setResourceId(all[0].id);
  }, [all, resourceId]);
  useEffect(() => {
    if (!packageId && deskPackages.length > 0) setPackageId(deskPackages[0].id);
  }, [deskPackages, packageId]);

  const isFloor = resource?.kind === 'floor';
  const deskPackage = deskPackages.find((p) => p.id === packageId) ?? deskPackages[0];
  const freeTrial = isFloor && deskPackage?.id === FREE_TRIAL_PACKAGE_ITEM_ID;
  const trialWindowValid =
    !freeTrial ||
    (isFreeTrialBookingDate(date) &&
      /^(?:[01]\d|2[0-3]):00$/.test(trialStartTime) &&
      /^(?:[01]\d|2[0-3]):00$/.test(trialEndTime) &&
      trialStartTime >= FREE_TRIAL_START_TIME &&
      trialStartTime <= FREE_TRIAL_LATEST_START_TIME &&
      trialEndTime >= FREE_TRIAL_EARLIEST_END_TIME &&
      trialEndTime <= FREE_TRIAL_END_TIME &&
      trialEndTime > trialStartTime);
  const trialStartMinute = freeTrial && trialWindowValid ? minutesOf(trialStartTime) : undefined;
  const trialEndMinute = freeTrial && trialWindowValid ? minutesOf(trialEndTime) : undefined;
  const floorAvailabilityReady = !isFloor || (Boolean(deskPackage) && trialWindowValid);
  // The package is picked in the booking popup. Until it is complete (a trial
  // needs its hours) the board is read without one, so the map still works.
  const boardPackageId = isFloor && floorAvailabilityReady ? deskPackage?.id : undefined;

  /**
   * The board for this space on this date, straight from the API.
   *
   * Read again before every attempt and after every write: the server retakes
   * the lock and rechecks occupancy as it writes, so a stale board loses the
   * race rather than winning it.
   */
  const availability = useQuery({
    queryKey: [
      'availability',
      resource?.id,
      date,
      boardPackageId,
      trialStartMinute,
      trialEndMinute,
    ],
    queryFn: () =>
      bookings.getAvailability(
        resource?.id as string,
        date,
        boardPackageId,
        trialStartMinute,
        trialEndMinute,
      ),
    enabled: mayRead && !!resource,
    // Changing the package in the popup re-reads the board; keeping the last
    // one meanwhile stops the popup over it from closing and reopening.
    placeholderData: keepPreviousData,
  });
  const board = availability.data ?? null;
  const slots = useMemo(() => board?.slots ?? [], [board]);
  const desks = useMemo(() => board?.desks ?? [], [board]);

  const detail = useQuery({
    queryKey: ['booking', openBookingId],
    queryFn: () => bookings.getBooking(openBookingId as string),
    enabled: openBookingId !== null,
  });
  const held = detail.data ?? null;

  /** A desk the map has selected, as the API's availability row for it. */
  const deskRow = useMemo(
    () =>
      desk === null
        ? null
        : (desks.find((d) => normaliseDeskId(d.deskId) === normaliseDeskId(desk)) ?? null),
    [desk, desks],
  );

  const unavailableDeskIds = useMemo(
    () => desks.filter((d) => !d.available || !d.isActive).map((d) => d.deskId),
    [desks],
  );

  const deskWithdrawn = deskRow !== null && !deskRow.isActive;
  const deskTaken = deskRow !== null && !deskRow.available && deskRow.isActive;

  const minutes = slot ? slot.to - slot.from : 0;
  const price = isFloor
    ? (deskPackage?.amountMinor ?? 0)
    : resource
      ? priceFor(resource, minutes)
      : 0;

  // A student package can be booked unpaid, but money cannot be taken until
  // someone records what they checked.
  const studentPackage =
    isFloor && deskPackage?.package.eligibility.kind === 'student_verification';

  const packageRules = useMemo(() => packageChoiceSchema(deskPackages), [deskPackages]);
  const packageChoice = useMemo(() => ({ packageId }), [packageId]);
  const packageValidation = useValidation(packageRules, packageChoice);

  /** The booking itself; a new customer is checked by `customerValidation`. */
  const bookingRules = useMemo(
    () => bookingSchema({ existingCustomer: mode === 'existing', studentPackage, price }),
    [mode, studentPackage, price],
  );
  const bookingValidation = useValidation(bookingRules, form);

  const paymentRules = useMemo(() => paymentSchema(held), [held]);
  const paymentValidation = useValidation(paymentRules, payment);

  const cancelDraft = useMemo(() => ({ reason }), [reason]);
  const cancelValidation = useValidation(cancelSchema, cancelDraft);

  const resourceRules = useMemo(
    () => resourceSchema({ creating, spaceRates, others: all }),
    [creating, spaceRates, all],
  );
  const resourceValidation = useValidation(resourceRules, draft ?? blankDraft);

  const resetPackage = packageValidation.reset;
  /**
   * Back to a clean board. The package goes back to the default too (the
   * effect above re-seeds the first one on sale): the map is read for the
   * package, and nothing on it says which one, so a Monthly left over from
   * the last booking would mark desks taken that are free today.
   */
  const reset = useCallback(() => {
    setDesk(null);
    setPackageId('');
    setChoosing(false);
    setSlot(null);
    setRunReady(false);
    setOpenBookingId(null);
    setError(null);
    setPanelError(null);
    resetPackage();
  }, [resetPackage]);

  const changeResource = (next: string) => {
    setResourceId(next);
    reset();
  };

  const handleDeskSelect = useCallback(
    (deskId: string | null) => {
      const row =
        deskId === null
          ? null
          : desks.find((d) => normaliseDeskId(d.deskId) === normaliseDeskId(deskId));
      setDesk(deskId);
      setChoosing(mayBook && Boolean(row?.available && row.isActive));
      setOpenBookingId(null);
      setError(null);
    },
    [desks, mayBook],
  );

  // Opening a held desk reads the booking behind it; the map only knows who it
  // belongs to, not what was paid.
  useEffect(() => {
    // Not while booking it: a package that runs into someone else's days marks
    // the desk held, and that is said in the popup rather than by opening theirs.
    if (deskRow?.booking && !choosing) setOpenBookingId(deskRow.booking.id);
  }, [deskRow, choosing]);

  const canBook =
    mayBook &&
    (isFloor ? floorAvailabilityReady && deskRow !== null && deskRow.available : Boolean(slot));

  /**
   * A desk is one click, so picking it opens the booking. A room is a run of
   * slots built up click by click, so it waits to be told the run is done —
   * otherwise the popup covers the calendar it is still being picked on.
   */
  const pickerOpen = (isFloor ? choosing : canBook && runReady) && !formOpen;
  /** The board is the one for the package on screen, not the last one kept. */
  const boardCurrent = !availability.isPlaceholderData && !availability.isFetching;
  const deskBookable = canBook && boardCurrent;

  /** Closing drops a desk, but keeps a room run so it can be adjusted. */
  const closePicker = () => {
    if (isFloor) reset();
    else setRunReady(false);
  };

  const refreshBoard = async (bookingId?: string | null) => {
    await queryClient.invalidateQueries({ queryKey: ['availability'] });
    await queryClient.invalidateQueries({ queryKey: ['bookings'] });
    await queryClient.invalidateQueries({ queryKey: ['payments'] });
    if (bookingId) await queryClient.invalidateQueries({ queryKey: ['booking', bookingId] });
  };

  const openForm = () => {
    if (!canBook || !resource) return;
    // With no package on sale the picker is not drawn, so there is nothing to
    // check; the server says why the desk cannot be booked.
    if (isFloor && deskPackages.length > 0 && !packageValidation.check()) return;
    if (!isFloor && slot) {
      if (slot.to - slot.from < minRun(resource)) {
        setError(`${resource.name} has a ${minRun(resource) / 60}-hour minimum.`);
        return;
      }
      if (!runAvailable(slots, slot.from, slot.to)) {
        setError('That run overlaps a booking that is already held.');
        return;
      }
    }
    setForm({ ...blankForm, settle: freeTrial ? 'none' : 'full' });
    bookingValidation.reset();
    customerValidation.reset();
    setMode(maySeeCustomers ? 'existing' : 'new');
    setError(null);
    setFormOpen(true);
  };

  const confirmBooking = async () => {
    if (!resource) return;

    // Both halves are checked, so every problem shows at once.
    const bookingValid = bookingValidation.check();
    const customerValid = mode === 'existing' || customerValidation.check();
    if (!bookingValid || !customerValid) return;

    const who: Pick<CreateAdminBookingRequest, 'customerId' | 'newCustomer'> =
      mode === 'existing'
        ? { customerId: form.customerId }
        : { newCustomer: createRequestOf(form.customer) };

    // The server prices the booking itself; this is the same arithmetic on the
    // same active rate card, sent so it can check the deposit rule against it.
    const initialPayment =
      freeTrial || form.settle === 'none'
        ? undefined
        : {
            amountMinor: form.settle === 'deposit' ? depositAmount(price) : price,
            method: form.method,
            ...(form.method === 'other' ? { methodDetail: form.methodDetail.trim() } : {}),
          };

    const allocation = isFloor
      ? {
          deskId: deskRow?.id ?? '',
          deskPackageItemId: packageId,
          ...(freeTrial ? { startMinute: trialStartMinute!, endMinute: trialEndMinute! } : {}),
        }
      : { startMinute: slot?.from ?? 0, endMinute: slot?.to ?? 0 };

    setBooking(true);
    setError(null);
    try {
      const created = await bookings.createBooking({
        resourceId: resource.id,
        bookingDate: date,
        ...allocation,
        ...(form.note.trim() ? { internalNote: form.note.trim() } : {}),
        ...(initialPayment ? { initialPayment } : {}),
        ...(studentPackage && form.evidenceNote.trim()
          ? { packageEligibilityVerification: { evidenceNote: form.evidenceNote.trim() } }
          : {}),
        ...who,
      } as CreateAdminBookingRequest);
      await refreshBoard(created.id);
      setFormOpen(false);
      setForm(blankForm);
      setSlot(null);
      setRunReady(false);
      setDesk(null);
      // As `reset` does: the next desk starts from the default package.
      setPackageId('');
      setOpenBookingId(created.id);
    } catch (e) {
      if (isBookingConflict(e)) {
        await refreshBoard();
        setError(
          'That desk or time was taken while this form was open. Nothing was booked, and the board above has been reloaded.',
        );
      } else {
        setError(e instanceof ApiError ? e.message : 'That did not book. Try again.');
      }
    } finally {
      setBooking(false);
    }
  };

  const collect = async () => {
    if (!held) return;
    if (!paymentValidation.check()) return;
    setBusy(true);
    setPanelError(null);
    try {
      await bookings.recordBookingPayment(held.id, {
        amountMinor: collectableAmount(held, payment.choice),
        expectedPaidAmountMinor: held.payment.paidAmountMinor,
        method: payment.method,
        ...(payment.method === 'other' ? { methodDetail: payment.methodDetail.trim() } : {}),
        ...(payment.note.trim() ? { note: payment.note.trim() } : {}),
        ...eligibilityPayload(held, payment),
      });
      await refreshBoard(held.id);
      setCollecting(false);
    } catch (e) {
      if (isPaymentStateChanged(e) || isBookingConflict(e)) {
        await refreshBoard(held.id);
        setPanelError(
          isPaymentStateChanged(e)
            ? 'Someone else took a payment on this booking. The figures have been reloaded. Check them before recording again.'
            : 'The desk or time was taken while this was open, so no payment was recorded.',
        );
      } else {
        setPanelError(e instanceof ApiError ? e.message : 'That did not record. Try again.');
      }
    } finally {
      setBusy(false);
    }
  };

  const release = async () => {
    if (!held) return;
    if (!cancelValidation.check()) return;
    setBusy(true);
    setPanelError(null);
    try {
      await bookings.cancelBooking(held.id, reason.trim() ? { reason: reason.trim() } : {});
      await refreshBoard(held.id);
      setCancelling(false);
      setReason('');
      reset();
    } catch (e) {
      setPanelError(e instanceof ApiError ? e.message : 'That did not cancel. Try again.');
    } finally {
      setBusy(false);
    }
  };

  /* ------------------------------------------------------------ inventory --- */

  /** The server's copy of the space being edited — see `startEdit`. */
  const [editingMedia, setEditingMedia] = useState<AdminResource['media']>([]);
  const [opening, setOpening] = useState(false);

  // Seeded from a fresh read rather than the inventory row: hours, pictures and
  // desks change from other sessions, and saving the hours replaces the whole
  // week, so a stale row would quietly undo someone else's edit.
  const startEdit = async () => {
    if (!resource) return;
    setOpening(true);
    setEditorError(null);
    setCreating(false);
    resourceValidation.reset();
    try {
      const fresh = await resources.getResource(resource.id);
      setEditingMedia(fresh.media);
      setDraft(draftOfResource(fresh));
    } catch {
      // Still editable from the inventory copy, but say so: saving replaces the
      // whole week of hours with what this form holds.
      setEditingMedia(resource.media);
      setDraft(draftOfResource(resource));
      setEditorError(
        'The latest copy of this space could not be read, so this form shows the one from the list. Check the hours before saving.',
      );
    } finally {
      setOpening(false);
    }
  };

  const startCreate = () => {
    setCreating(true);
    setEditorError(null);
    resourceValidation.reset();
    setEditingMedia([]);
    setDraft({ ...blankDraft, displayOrder: String(all.length) });
  };

  // An API from before booking rules leaves the field out, and refuses it on save
  // (its schemas are strict), so the field is only sent, and the section only
  // unlocked, where the API is known to take it.
  const rulesSupported = resource !== null && 'bookingRules' in resource;

  const saveResource = async () => {
    if (!draft || !resource) return;
    if (!resourceValidation.check()) return;
    const isFloorDraft = draft.kind === 'floor';
    // Made from the name, which `resourceSchema` has already checked is free.
    const slug = creating ? slugFromName(draft.name) : draft.slug;
    const rulesPayload = rulesSupported
      ? {
          bookingRules: {
            booking: draft.bookingRules.booking.trim(),
            hours: draft.bookingRules.hours.trim(),
            payment: draft.bookingRules.payment.trim(),
            cancellation: draft.bookingRules.cancellation.trim(),
          },
        }
      : {};

    setSaving(true);
    setEditorError(null);
    try {
      if (creating) {
        const created = await resources.createResource({
          facilityId: resource.facility.id,
          rateCardId: resource.rate.rateCardId,
          slug,
          name: draft.name.trim(),
          kind: draft.kind,
          bookingMode: draft.bookingMode,
          rateItemId: draft.rateItemId,
          note: draft.note.trim(),
          shortDescription: draft.shortDescription.trim(),
          longDescription: draft.longDescription.trim(),
          amenities: draft.amenities,
          ...rulesPayload,
          mediaFileIds: draft.mediaFileIds,
          isActive: draft.isActive,
          displayOrder: Number(draft.displayOrder) || 0,
          ...(isFloorDraft
            ? { seatMapKey: draft.seatMapKey }
            : {
                capacity: Number(draft.capacity) || null,
                minimumBookingMinutes: Number(draft.minimumBookingMinutes) || null,
                operatingHours: operatingHoursOf(draft.hours),
              }),
        });
        setResourceId(created.id);
      } else {
        await resources.updateResource(resource.id, {
          name: draft.name.trim(),
          bookingMode: draft.bookingMode,
          rateItemId: draft.rateItemId,
          note: draft.note.trim(),
          shortDescription: draft.shortDescription.trim(),
          longDescription: draft.longDescription.trim(),
          amenities: draft.amenities,
          ...rulesPayload,
          mediaFileIds: draft.mediaFileIds,
          isActive: draft.isActive,
          displayOrder: Number(draft.displayOrder) || 0,
          ...(isFloorDraft
            ? {}
            : {
                capacity: Number(draft.capacity) || null,
                minimumBookingMinutes: Number(draft.minimumBookingMinutes) || null,
                operatingHours: operatingHoursOf(draft.hours),
              }),
        });
      }
      await queryClient.invalidateQueries({ queryKey: ['resources'] });
      await queryClient.invalidateQueries({ queryKey: ['availability'] });
      setDraft(null);
      reset();
    } catch (e) {
      setEditorError(saveProblem(e));
    } finally {
      setSaving(false);
    }
  };

  const toggleDesk = async (target: { id: string; isActive: boolean }) => {
    if (!resource) return;
    setDeskBusyId(target.id);
    setEditorError(null);
    try {
      await resources.setDeskActive(resource.id, target.id, !target.isActive);
      await queryClient.invalidateQueries({ queryKey: ['resources'] });
      await queryClient.invalidateQueries({ queryKey: ['availability'] });
    } catch (e) {
      setEditorError(e instanceof ApiError ? e.message : 'That desk did not change. Try again.');
    } finally {
      setDeskBusyId(null);
    }
  };

  if (inventory.isPending || card.isPending) {
    return <Card title="Spaces">Loading the space inventory…</Card>;
  }
  if (inventory.isError || !resource) {
    return (
      <EmptyState
        title="No bookable space"
        hint={
          inventory.error instanceof ApiError
            ? inventory.error.message
            : 'Nothing is set up in the inventory yet.'
        }
      />
    );
  }

  const takenDesks = desks.filter((d) => !d.available).length;
  const takenSlots = slots.filter((s) => !s.available).length;
  const seats = resource.activeDeskCount;

  return (
    <div className="flex flex-col gap-6">
      <div className="grid gap-4 sm:grid-cols-3">
        <StatCard
          label={isFloor ? 'Desks free' : 'Slots free'}
          value={String(
            isFloor
              ? desks.filter((d) => d.available && d.isActive).length
              : slots.filter((s) => s.available).length,
          )}
          note={`${resource.name} · ${date}`}
        />
        <StatCard
          label={isFloor ? 'Desks taken' : 'Slots taken'}
          value={String(isFloor ? takenDesks : takenSlots)}
          note={
            isFloor
              ? `of ${seats} in service${
                  resource.deskCount > seats ? ` · ${resource.deskCount - seats} withdrawn` : ''
                }`
              : `of ${slots.length} hourly slots`
          }
        />
        <StatCard
          label="Rate"
          value={
            isFloor
              ? formatCents(deskPackage?.amountMinor ?? 0)
              : formatCents(resource.rate.amountMinor)
          }
          note={
            isFloor
              ? `${deskPackage?.name ?? 'No package'} · per ${deskPackage?.unit ?? 'day'}`
              : `per ${resource.rate.unit}`
          }
        />
      </div>

      {/* The side panel only earns a column once there is a booking to show in it. */}
      <div
        className={
          'grid gap-6' + (openBookingId ? ' xl:grid-cols-[minmax(0,1.85fr)_minmax(320px,1fr)]' : '')
        }
      >
        <Card
          title={resource.name}
          action={
            <Toolbar>
              {/* One picker for every bookable space — floors and rooms alike. */}
              <Select
                value={resource.id}
                onChange={changeResource}
                options={all.map((r) => ({
                  value: r.id,
                  label: r.isActive ? r.name : `${r.name} (not bookable)`,
                }))}
                aria-label="Space"
              />
              {/* Rooms take their date from the calendar below, so there is never
                  a second competing date control. */}
              {isFloor && (
                <input
                  type="date"
                  value={date}
                  min={freeTrial ? FREE_TRIAL_BOOKING_START_DATE : undefined}
                  max={freeTrial ? FREE_TRIAL_BOOKING_END_DATE : undefined}
                  onChange={(e) => {
                    setDate(e.target.value);
                    reset();
                  }}
                  aria-label="Booking date"
                  className={controlCls + ' w-40'}
                />
              )}
              {mayManage && (
                <>
                  <Button variant="quiet" onClick={() => void startEdit()} disabled={opening}>
                    {opening ? 'Opening…' : 'Edit space'}
                  </Button>
                  <Button variant="quiet" onClick={startCreate}>
                    Add space
                  </Button>
                </>
              )}
            </Toolbar>
          }
        >
          {!resource.isActive && (
            <div className="mb-4">
              <Chip className="border border-warn-edge bg-warn-wash text-warn-text">
                Not bookable
              </Chip>
            </div>
          )}

          {!mayRead ? (
            <EmptyState
              title="Availability is not available to your account"
              hint="Ask a super administrator for permission to read reservations."
            />
          ) : availability.isError ? (
            <EmptyState
              title="Could not read availability"
              hint={
                availability.error instanceof ApiError
                  ? availability.error.message
                  : 'The server could not be reached.'
              }
            />
          ) : isFloor && resource.seatMapKey ? (
            <SeatMap
              space={resource.seatMapKey as SpaceId}
              selected={desk}
              onSelect={handleDeskSelect}
              booked={unavailableDeskIds}
              allowBookedSelection
              showHeader={false}
              showPicker={false}
              showFooter={false}
              showLegend={false}
              trimFrame
              style={MAP_LAYOUT}
            />
          ) : isFloor ? (
            <EmptyState
              title="No seat map"
              hint="This floor has no seat-map artwork, so its desks cannot be drawn."
            />
          ) : (
            <BookingCalendar
              resource={resource}
              date={date}
              onDateChange={(next) => {
                setDate(next);
                reset();
              }}
              slots={slots}
              openFromMinute={board?.openFromMinute ?? null}
              openToMinute={board?.openToMinute ?? null}
              selection={slot}
              onSelect={(next) => {
                setSlot(next);
                setOpenBookingId(null);
                setError(null);
              }}
              onOpenBooking={(bookingId) => {
                setOpenBookingId(bookingId);
                setSlot(null);
                setRunReady(false);
              }}
              onContinue={() => setRunReady(true)}
              closedNote={`${resource.name} is closed on this day. Its opening hours are set on the room.`}
            />
          )}

          {availability.isPending && mayRead && floorAvailabilityReady && (
            <p className="m-0 mt-4 text-base text-ink-muted">Reading availability…</p>
          )}

          {deskWithdrawn && (
            <p className="m-0 mt-4 rounded-xl border border-warn-edge bg-warn-wash px-4 py-3 text-base text-warn-text">
              Desk {desk} is out of service. Put it back from Edit space before booking it.
            </p>
          )}
          {deskTaken && !held && (
            <p className="m-0 mt-4 text-base text-ink-muted">Reading the booking on desk {desk}…</p>
          )}
        </Card>

        {openBookingId && (
          <Card title="Booking">
            {detail.isPending ? (
              <p className="m-0 text-base text-ink-muted">Loading the booking…</p>
            ) : detail.isError || !held ? (
              <Banner>
                {detail.error instanceof ApiError
                  ? detail.error.message
                  : 'That booking could not be read.'}
              </Banner>
            ) : (
              <div className="flex flex-col gap-4">
                {panelError && <Banner>{panelError}</Banner>}
                <BookingDetails
                  booking={held}
                  busy={busy}
                  onCollect={
                    mayRecord &&
                    held.payment.balanceAmountMinor > 0 &&
                    held.lifecycleStatus !== 'cancelled'
                      ? () => {
                          setPayment(blankPaymentDraft);
                          paymentValidation.reset();
                          setPanelError(null);
                          setCollecting(true);
                        }
                      : undefined
                  }
                  onCancel={
                    mayBook && held.lifecycleStatus !== 'cancelled'
                      ? () => {
                          setReason('');
                          cancelValidation.reset();
                          setPanelError(null);
                          setCancelling(true);
                        }
                      : undefined
                  }
                />
              </div>
            )}
          </Card>
        )}
      </div>

      {/* Picking a free desk or slot opens the booking there and then, over the map. */}
      <Modal
        open={pickerOpen}
        onClose={closePicker}
        title="New booking"
        footer={
          <>
            <Button onClick={openForm} disabled={!deskBookable}>
              Book {isFloor ? `desk ${desk}` : 'this run'}
            </Button>
            <Button variant="quiet" onClick={closePicker}>
              {isFloor ? 'Cancel' : 'Change time'}
            </Button>
          </>
        }
      >
        <div className="flex flex-wrap gap-2">
          <Chip className="border border-brand-edge/40 bg-brand-wash text-brand-strong">
            Available
          </Chip>
          <Chip>{isFloor ? `Desk ${desk}` : clockRange(slot?.from ?? 0, slot?.to ?? 0)}</Chip>
          <Chip>{date}</Chip>
          {freeTrial && trialWindowValid && (
            <Chip>{clockRange(trialStartMinute!, trialEndMinute!)}</Chip>
          )}
        </div>
        {isFloor ? (
          /* Desks are sold by package, not by the hour. */
          <>
            {deskPackages.length > 0 && (
              <PackagePicker
                packages={deskPackages}
                value={packageId}
                onChange={(next) => {
                  setPackageId(next);
                  // The trial runs only in its own week, and the date control is
                  // behind this popup: move the date there rather than ask for it.
                  // The board re-reads for the new day, so the desk is checked again.
                  if (next === FREE_TRIAL_PACKAGE_ITEM_ID && !isFreeTrialBookingDate(date)) {
                    setDate(FREE_TRIAL_BOOKING_START_DATE);
                  }
                  setError(null);
                  packageValidation.reset();
                }}
                variant="select"
                {...packageValidation.field('packageId')}
              />
            )}
            {freeTrial && (
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Trial starts" required>
                  <input
                    type="time"
                    step={FREE_TRIAL_STEP_MINUTES * 60}
                    min={FREE_TRIAL_START_TIME}
                    max={FREE_TRIAL_LATEST_START_TIME}
                    className={inputCls}
                    value={trialStartTime}
                    onChange={(event) => setTrialStartTime(event.target.value)}
                  />
                </Field>
                <Field
                  label="Trial ends"
                  required
                  error={
                    trialStartTime && trialEndTime && trialEndTime <= trialStartTime
                      ? 'End time must be after start time.'
                      : undefined
                  }
                >
                  <input
                    type="time"
                    step={FREE_TRIAL_STEP_MINUTES * 60}
                    min={FREE_TRIAL_EARLIEST_END_TIME}
                    max={FREE_TRIAL_END_TIME}
                    className={inputCls}
                    value={trialEndTime}
                    onChange={(event) => setTrialEndTime(event.target.value)}
                  />
                </Field>
              </div>
            )}
            {freeTrial && !trialWindowValid ? (
              <p className="m-0 text-base text-ink-muted">
                Choose a date from {FREE_TRIAL_BOOKING_WINDOW_LABEL} and times during{' '}
                {FREE_TRIAL_TIME_WINDOW_LABEL}, on the hour.
              </p>
            ) : !boardCurrent ? (
              <p className="m-0 text-base text-ink-muted">Checking desk {desk}…</p>
            ) : (
              !canBook && (
                <Banner>
                  Desk {desk} is not free for {deskPackage?.name ?? 'this package'}. Pick another
                  package, or close this and pick another desk.
                </Banner>
              )
            )}
          </>
        ) : (
          <p className="m-0 text-base text-ink-secondary">
            {`${minutes / 60} ${minutes === 60 ? 'hour' : 'hours'} in ${resource.name}.`}
          </p>
        )}
        <div className="flex items-center justify-between rounded-xl border border-hairline bg-raised px-4 py-3">
          <span className="text-base text-ink-muted">Total</span>
          <span className="text-xl font-semibold tabular-nums">{formatCents(price)}</span>
        </div>
        {error && <Banner>{error}</Banner>}
      </Modal>

      <Modal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        title={`Book ${resource.name}`}
        footer={
          <>
            <Button onClick={() => void confirmBooking()} disabled={booking}>
              {booking ? 'Booking…' : 'Confirm booking'}
            </Button>
            <Button variant="quiet" disabled={booking} onClick={() => setFormOpen(false)}>
              Cancel
            </Button>
          </>
        }
      >
        <div className="flex flex-wrap gap-2">
          <Chip>{isFloor ? `Desk ${desk}` : clockRange(slot?.from ?? 0, slot?.to ?? 0)}</Chip>
          {isFloor && deskPackage && <Chip>{deskPackage.name}</Chip>}
          <Chip>{date}</Chip>
          {freeTrial && trialWindowValid && (
            <Chip>{clockRange(trialStartMinute!, trialEndMinute!)}</Chip>
          )}
          <Chip>{formatCents(price)}</Chip>
          {form.settle === 'deposit' && (
            <Chip className="border border-warn-edge bg-warn-wash text-warn-text">
              {formatCents(price - depositAmount(price))} owing
            </Chip>
          )}
        </div>

        {maySeeCustomers && (
          <div className="flex overflow-hidden rounded-xl border border-hairline">
            {(
              [
                ['existing', 'Existing customer'],
                ['new', 'New customer'],
              ] as const
            ).map(([value, label]) => (
              <button
                key={value}
                type="button"
                onClick={() => {
                  setMode(value);
                  setError(null);
                }}
                aria-pressed={mode === value}
                className={
                  'min-h-(--target-control) flex-1 px-4 text-base font-semibold transition-colors focus-visible:outline-(length:--focus-ring-width) focus-visible:outline-offset-[-3px] focus-visible:outline-(--focus-ring-color) ' +
                  (mode === value
                    ? 'bg-cell-selected-bg text-cell-selected-fg'
                    : 'bg-surface text-ink-muted hover:text-brand-text')
                }
              >
                {label}
              </button>
            ))}
          </div>
        )}

        {mode === 'existing' ? (
          <Field
            label="Customer"
            hint="Type a name, reference, email or phone. Add anyone new as a new customer."
            required
            {...bookingValidation.field('customerId')}
          >
            <CustomerPicker
              value={form.customerId}
              onChange={(customerId) => setForm({ ...form, customerId })}
              aria-label="Customer"
            />
          </Field>
        ) : (
          <CustomerForm
            draft={form.customer}
            onChange={(customer) => setForm({ ...form, customer })}
            field={customerValidation.field}
          />
        )}

        {freeTrial ? (
          <div className="rounded-xl border border-brand-edge/40 bg-brand-wash px-4 py-3 text-base text-brand-strong">
            Free trial · no payment is required. The booking will be confirmed immediately.
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label="Paying"
              hint={
                form.settle === 'none'
                  ? 'An unpaid booking holds nothing until money is taken.'
                  : undefined
              }
              required
              {...bookingValidation.field('settle')}
            >
              <Select
                variant="field"
                className={selectCls}
                value={form.settle}
                onChange={(next) => {
                  setForm({ ...form, settle: next as Settle });
                  setError(null);
                }}
                options={[
                  { value: 'full', label: `Full amount: ${formatCents(price)}` },
                  {
                    value: 'deposit',
                    label: `${DEPOSIT_PERCENTAGE}% deposit: ${formatCents(depositAmount(price))}`,
                  },
                  { value: 'none', label: 'Nothing yet' },
                ]}
                aria-label="Paying"
              />
            </Field>
            {form.settle !== 'none' && (
              <Field label="Method" required {...bookingValidation.field('method')}>
                <Select
                  variant="field"
                  className={selectCls}
                  value={form.method}
                  onChange={(m) =>
                    setForm({
                      ...form,
                      method: m as BookingPaymentMethod,
                      methodDetail: m === 'other' ? form.methodDetail : '',
                    })
                  }
                  options={PAYMENT_METHOD_OPTIONS}
                  aria-label="Payment method"
                />
              </Field>
            )}
            {form.settle !== 'none' && form.method === 'other' && (
              <Field
                label="What was it"
                hint="Cheque, bank transfer, and so on."
                required
                {...bookingValidation.field('methodDetail')}
              >
                <input
                  className={inputCls}
                  value={form.methodDetail}
                  maxLength={80}
                  placeholder="Office cheque"
                  onChange={(e) => setForm({ ...form, methodDetail: e.target.value })}
                />
              </Field>
            )}
          </div>
        )}

        {studentPackage && deskPackage && (
          <div className="flex flex-col gap-3 rounded-xl border border-warn-edge bg-warn-wash px-4 py-3">
            <span className="text-base font-semibold text-warn-text">
              Student package: check eligibility
            </span>
            {deskPackage.package.eligibility.instructions && (
              <span className="text-base text-warn-text">
                {deskPackage.package.eligibility.instructions}
              </span>
            )}
            <Field
              label="What you checked"
              hint={
                form.settle === 'none'
                  ? 'Optional now; required before the first payment is taken.'
                  : 'Required before taking payment. Recorded on the booking with your name.'
              }
              required={form.settle !== 'none'}
              {...bookingValidation.field('evidenceNote')}
            >
              <input
                className={inputCls}
                value={form.evidenceNote}
                maxLength={500}
                placeholder="Current student card checked; expires June 2027."
                onChange={(e) => setForm({ ...form, evidenceNote: e.target.value })}
              />
            </Field>
          </div>
        )}

        <Field
          label="Internal note"
          hint="Optional. Kept on the booking, not sent to anyone."
          {...bookingValidation.field('note')}
        >
          <input
            className={inputCls}
            value={form.note}
            placeholder="Quiet desk requested."
            onChange={(e) => setForm({ ...form, note: e.target.value })}
          />
        </Field>

        {error && <Banner>{error}</Banner>}
      </Modal>

      <Modal
        open={collecting && held !== null}
        onClose={() => setCollecting(false)}
        title="Record payment"
        footer={
          <>
            <Button onClick={() => void collect()} disabled={busy}>
              {busy ? 'Recording…' : 'Record payment'}
            </Button>
            <Button variant="quiet" disabled={busy} onClick={() => setCollecting(false)}>
              Cancel
            </Button>
          </>
        }
      >
        {panelError && <Banner>{panelError}</Banner>}
        {held && (
          <PaymentFields
            booking={held}
            draft={payment}
            onChange={setPayment}
            field={paymentValidation.field}
          />
        )}
      </Modal>

      <Modal
        open={cancelling && held !== null}
        onClose={() => setCancelling(false)}
        title="Release this booking"
        footer={
          <>
            <Button variant="danger" onClick={() => void release()} disabled={busy}>
              {busy ? 'Cancelling…' : 'Cancel booking'}
            </Button>
            <Button variant="quiet" disabled={busy} onClick={() => setCancelling(false)}>
              Keep it
            </Button>
          </>
        }
      >
        {panelError && <Banner>{panelError}</Banner>}
        <p className="m-0 text-base text-ink-secondary">
          The desk or time goes back on the board and the booking is kept with its history. Any
          deposit already taken is non-refundable and stays on the record.
        </p>
        <Field
          label="Reason"
          hint="Optional, and written to the audit log."
          {...cancelValidation.field('reason')}
        >
          <input
            className={inputCls}
            value={reason}
            maxLength={500}
            placeholder="Customer cancelled by phone"
            onChange={(e) => setReason(e.target.value)}
          />
        </Field>
      </Modal>

      <Modal
        open={draft !== null}
        onClose={() => setDraft(null)}
        title={creating ? 'Add a space' : `Edit ${resource.name}`}
        footer={
          <>
            <Button onClick={() => void saveResource()} disabled={saving}>
              {saving ? 'Saving…' : creating ? 'Add space' : 'Save changes'}
            </Button>
            <Button variant="quiet" disabled={saving} onClick={() => setDraft(null)}>
              Cancel
            </Button>
          </>
        }
      >
        {editorError && <Banner ref={editorErrorRef}>{editorError}</Banner>}
        {draft && (
          <ResourceEditor
            draft={draft}
            onChange={setDraft}
            creating={creating}
            spaceRates={spaceRates}
            facilityName={resource.facility.name}
            desks={creating ? [] : resource.desks}
            deskBusyId={deskBusyId}
            onToggleDesk={(d) => void toggleDesk(d)}
            knownMedia={editingMedia}
            bookingRules={{
              supported: rulesSupported,
              defaults: creating || !rulesSupported ? undefined : resource.defaultBookingRules,
            }}
            field={resourceValidation.field}
          />
        )}
      </Modal>
    </div>
  );
}

function Banner({
  children,
  ref,
}: {
  children: React.ReactNode;
  ref?: React.Ref<HTMLParagraphElement>;
}) {
  return (
    <p
      ref={ref}
      role="alert"
      className="m-0 rounded-xl border border-danger-edge bg-danger-wash px-4 py-3 text-base text-danger-text"
    >
      {children}
    </p>
  );
}

/**
 * Why a space did not save, phrased for the administrator. A schedule that
 * would strand confirmed bookings names them, so they can be moved first.
 */
function saveProblem(error: unknown): string {
  if (!(error instanceof ApiError)) return 'That did not save. Try again.';
  const body = error.body as {
    code?: string;
    details?: { bookings?: { reference: string; bookingDate: string }[] };
  } | null;
  if (body?.code === 'OPERATING_HOURS_CONFLICT') {
    const stranded = body.details?.bookings ?? [];
    const list = stranded
      .slice(0, 5)
      .map((b) => `${b.reference} on ${dateStamp(b.bookingDate)}`)
      .join(', ');
    const more = stranded.length > 5 ? ` and ${stranded.length - 5} more` : '';
    return stranded.length > 0
      ? `Those hours would leave confirmed bookings outside them: ${list}${more}. Move or cancel them first, or keep the room open for them.`
      : error.message;
  }
  return error.message;
}
